/**
 * Ichimoku Cloud (Kumo) primitive for Lightweight Charts v5.
 * Draws a filled area between Senkou Span A and Senkou Span B,
 * green when A >= B (bullish) and red when A < B (bearish).
 *
 * Attach to any series on pane 0 via series.attachPrimitive(plugin).
 */

class IchimokuCloudRenderer {
  constructor(data) {
    this._data = data;
  }

  draw() {}

  drawBackground(target) {
    const points = this._data.points;
    if (!points || points.length < 2) return;

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const hratio = scope.horizontalPixelRatio;
      const vratio = scope.verticalPixelRatio;

      // Split into segments at crossover points and fill each segment
      let segStart = 0;

      for (let i = 1; i <= points.length; i++) {
        const prev = points[i - 1];
        const curr = i < points.length ? points[i] : null;

        // Detect crossover: sign of (spanA - spanB) changes
        const crossed = curr &&
          Math.sign(curr.spanA - curr.spanB) !== Math.sign(prev.spanA - prev.spanB);
        const isEnd = i === points.length;

        if (crossed || isEnd) {
          const segEnd = isEnd ? i - 1 : i;
          const segment = [];
          for (let j = segStart; j <= segEnd && j < points.length; j++) {
            segment.push(points[j]);
          }

          if (segment.length >= 2) {
            // Determine if bullish: in canvas coords, lower Y = higher price
            // so if spanA_y < spanB_y, spanA price is higher = bullish
            const isBullish = segment[0].spanA <= segment[0].spanB;

            const region = new Path2D();
            // Trace spanA left to right
            region.moveTo(segment[0].x * hratio, segment[0].spanA * vratio);
            for (const p of segment) {
              region.lineTo(p.x * hratio, p.spanA * vratio);
            }
            // Trace spanB right to left
            for (let j = segment.length - 1; j >= 0; j--) {
              region.lineTo(segment[j].x * hratio, segment[j].spanB * vratio);
            }
            region.closePath();

            ctx.fillStyle = isBullish
              ? 'rgba(34, 197, 94, 0.15)'
              : 'rgba(239, 68, 68, 0.15)';
            ctx.fill(region);
          }

          // Overlap by one point at crossover for seamless fill
          segStart = isEnd ? i : i - 1;
        }
      }
    });
  }
}

class IchimokuCloudPaneView {
  constructor(source) {
    this._source = source;
    this._data = { points: [] };
  }

  update() {
    const series = this._source._series;
    const chart = this._source._chart;
    if (!series || !chart) {
      this._data = { points: [] };
      return;
    }

    const timeScale = chart.timeScale();
    const spanAData = this._source._spanAData;
    const spanBData = this._source._spanBData;

    if (!spanAData || !spanBData || spanAData.length === 0 || spanBData.length === 0) {
      this._data = { points: [] };
      return;
    }

    // Build Map for spanB by time
    const bMap = new Map();
    for (const p of spanBData) bMap.set(p.time, p.value);

    const points = [];
    for (const a of spanAData) {
      const bVal = bMap.get(a.time);
      if (bVal === undefined) continue;

      const x = timeScale.timeToCoordinate(a.time);
      if (x === null) continue;

      const spanAY = series.priceToCoordinate(a.value);
      const spanBY = series.priceToCoordinate(bVal);
      if (spanAY === null || spanBY === null) continue;

      points.push({ x, spanA: spanAY, spanB: spanBY });
    }

    this._data = { points };
  }

  renderer() {
    return new IchimokuCloudRenderer(this._data);
  }

  zOrder() {
    return 'bottom';
  }
}

export class IchimokuCloudPrimitive {
  constructor() {
    this._chart = null;
    this._series = null;
    this._spanAData = [];
    this._spanBData = [];
    this._paneView = new IchimokuCloudPaneView(this);
    this._requestUpdate = null;
  }

  attached({ chart, series, requestUpdate }) {
    this._chart = chart;
    this._series = series;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews() {
    return [this._paneView];
  }

  updateAllViews() {
    this._paneView.update();
  }

  /**
   * Set the cloud data (Senkou Span A and B arrays).
   * @param {Array} spanA - [{time, value}]
   * @param {Array} spanB - [{time, value}]
   */
  setData(spanA, spanB) {
    this._spanAData = spanA;
    this._spanBData = spanB;
    if (this._requestUpdate) this._requestUpdate();
  }
}
